import React, { useState, Component } from 'react';
import { XIcon, PlusIcon, SearchIcon } from 'lucide-react';
interface ComponentType {
  id: string;
  name: string;
  department: string;
  type?: string;
  children?: ComponentType[];
  expanded?: boolean;
  isGroup?: boolean;
}
interface AddComponentProps {
  onAdd: (component: ComponentType) => void;
  onCancel: () => void;
  parentComponent: ComponentType | null;
  existingComponents: ComponentType[];
}
export function AddComponent({
  onAdd,
  onCancel,
  parentComponent,
  existingComponents
}: AddComponentProps) {
  const [componentData, setComponentData] = useState<ComponentType>({
    id: '',
    name: '',
    department: parentComponent?.department || 'APZ',
    isGroup: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!componentData.id.trim() || !componentData.name.trim()) {
      alert('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }
    onAdd(componentData);
  };
  // Beispiel-Komponenten für die Suche
  const sampleComponents = [{
    id: 'R061307',
    name: 'MSC Alaska Seelachs',
    department: 'APZ'
  }, {
    id: 'R053408',
    name: 'Hähnchen Brust St gebraten',
    department: 'APZ'
  }, {
    id: 'R061304',
    name: 'MSC Miesmuschel',
    department: 'APZ'
  }, {
    id: 'R064375',
    name: 'MSC Kaltwassergarnelen',
    department: 'APZ'
  }, {
    id: 'R161215',
    name: 'Langkornreis',
    department: 'APZ'
  }, {
    id: 'R090533',
    name: 'Erbsen',
    department: 'APZ'
  }, {
    id: 'R092017',
    name: 'Tomate',
    department: 'APZ'
  }, {
    id: 'R092616',
    name: 'Zwiebeln',
    department: 'APZ'
  }, {
    id: 'R091606',
    name: 'Paprika',
    department: 'APZ'
  }, {
    id: 'R099035',
    name: 'Zuckererbsen',
    department: 'APZ'
  }, {
    id: 'R151501',
    name: 'Rapsöl raffiniert',
    department: 'APZ'
  }, {
    id: 'R192313',
    name: 'WASSER',
    department: 'APZ'
  }, {
    id: 'R140628',
    name: 'Fond Fisch',
    department: 'GKNass'
  }, {
    id: 'R140666',
    name: 'Fond',
    department: 'GKNass'
  }, {
    id: 'R142015',
    name: 'Tomatenmark',
    department: 'GKNass'
  }, {
    id: 'R142608',
    name: 'Zwiebeln',
    department: 'GKTrocken'
  }, {
    id: 'R141117',
    name: 'Knoblauch',
    department: 'GKTrocken'
  }, {
    id: 'R141616',
    name: 'Pfeffer',
    department: 'GKTrocken'
  }, {
    id: 'R141931',
    name: 'Salz',
    department: 'GKTrocken'
  }];
  const filteredComponents = sampleComponents.filter(component => component.id.toLowerCase().includes(searchTerm.toLowerCase()) || component.name.toLowerCase().includes(searchTerm.toLowerCase()));
  // Bestimme die Farbe für die Vorschau basierend auf der Abteilung
  const getDepartmentColor = (department: string) => {
    let bgColor = 'bg-white';
    let textColor = 'text-gray-800';
    // Hauptabteilungen - modernere, subtilere Farben
    if (department === 'Mischabteilung') {
      bgColor = 'bg-indigo-500 bg-opacity-85';
      textColor = 'text-white';
    } else if (department === 'Kochabteilung') {
      bgColor = 'bg-violet-500 bg-opacity-85';
      textColor = 'text-white';
    } else if (department === 'Gewürzküche') {
      bgColor = 'bg-emerald-500 bg-opacity-85';
      textColor = 'text-white';
    }
    // Unterabteilungen der Gewürzküche - sanftere Farben
    else if (department === 'GKNass') {
      bgColor = 'bg-emerald-100';
      textColor = 'text-emerald-900';
    } else if (department === 'GKTrocken') {
      bgColor = 'bg-rose-100';
      textColor = 'text-rose-900';
    }
    // APZ-Rohstoffe mit unterschiedlichen Farben je nach übergeordneter Komponente
    else if (department === 'APZ') {
      if (parentComponent?.department === 'Mischabteilung') {
        bgColor = 'bg-indigo-50';
        textColor = 'text-indigo-900';
      } else if (parentComponent?.department === 'Kochabteilung') {
        bgColor = 'bg-violet-50';
        textColor = 'text-violet-900';
      } else {
        bgColor = 'bg-gray-50';
        textColor = 'text-gray-800';
      }
    }
    return {
      bgColor,
      textColor
    };
  };
  const {
    bgColor,
    textColor
  } = getDepartmentColor(componentData.department);
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-7">
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-8">
        <div className="flex justify-between items-center mb-4" data-id="element-9">
          <h3 className="text-lg font-bold text-gray-800" data-id="element-10">
            {parentComponent ? `Unterkomponente zu "${parentComponent.name}" hinzufügen` : 'Neue Komponente hinzufügen'}
          </h3>
          <button onClick={onCancel} className="p-1" data-id="element-11">
            <XIcon size={20} data-id="element-12" />
          </button>
        </div>
        <div className="mb-6" data-id="element-13">
          <div className="flex gap-4 mb-4" data-id="element-14">
            
          </div>
          {searchTerm && <div className="mb-6 border border-gray-200 rounded-md overflow-hidden max-h-60 overflow-y-auto" data-id="element-15">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-16">
                <thead className="bg-gray-50" data-id="element-17">
                  <tr data-id="element-18">
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19">
                      ID
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20">
                      Name
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-21">
                      Abteilung
                    </th>
                    <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22">
                      Aktion
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-23">
                  {filteredComponents.map(component => {
                const {
                  bgColor,
                  textColor
                } = getDepartmentColor(component.department);
                return <tr key={component.id} className={`${bgColor} ${textColor}`} data-id="element-24">
                        <td className="px-4 py-2 text-sm" data-id="element-25">{component.id}</td>
                        <td className="px-4 py-2 text-sm" data-id="element-26">{component.name}</td>
                        <td className="px-4 py-2 text-sm" data-id="element-27">
                          {component.department}
                        </td>
                        <td className="px-4 py-2 text-sm text-right" data-id="element-28">
                          <button onClick={() => {
                      setComponentData({
                        ...component,
                        isGroup: false
                      });
                    }} className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-xs" data-id="element-29">
                            Auswählen
                          </button>
                        </td>
                      </tr>;
              })}
                  {filteredComponents.length === 0 && <tr data-id="element-30">
                      <td colSpan={4} className="px-4 py-3 text-sm text-gray-500 text-center" data-id="element-31">
                        Keine Ergebnisse gefunden
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>}
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-32">
            <div className="grid grid-cols-2 gap-4" data-id="element-33">
              <div data-id="element-34">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-35">
                  Artikel-Nr. <span className="text-red-500" data-id="element-36">*</span>
                </label>
                <input type="text" className="w-full p-2 border border-gray-300 rounded-md" value={componentData.id} onChange={e => setComponentData({
                ...componentData,
                id: e.target.value
              })} required data-id="element-37" />
              </div>
              <div data-id="element-38">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-39">
                  Name <span className="text-red-500" data-id="element-40">*</span>
                </label>
                <input type="text" className="w-full p-2 border border-gray-300 rounded-md" value={componentData.name} onChange={e => setComponentData({
                ...componentData,
                name: e.target.value
              })} required data-id="element-41" />
              </div>
            </div>
            <div data-id="element-42">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-43">
                Abteilung <span className="text-red-500" data-id="element-44">*</span>
              </label>
              <select className="w-full p-2 border border-gray-300 rounded-md" value={componentData.department} onChange={e => setComponentData({
              ...componentData,
              department: e.target.value
            })} required data-id="element-45">
                <option value="APZ" data-id="element-46">APZ-Rohstoffe</option>
                <option value="Mischabteilung" data-id="element-47">Mischabteilung</option>
                <option value="Kochabteilung" data-id="element-48">Kochabteilung</option>
                <option value="Gewürzküche" data-id="element-49">Gewürzküche</option>
                <option value="GKNass" data-id="element-50">Gewürzküche - Nass</option>
                <option value="GKTrocken" data-id="element-51">Gewürzküche - Trocken</option>
              </select>
            </div>
            
            <div className="mt-4" data-id="element-52">
              <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-53">
                Vorschau:
              </h4>
              <div className={`${bgColor} ${textColor} p-3 rounded-md flex justify-between items-center`} data-id="element-54">
                <div data-id="element-55">
                  <span className="font-medium" data-id="element-56">{componentData.id}</span>
                  {componentData.id && componentData.name && <span className="mx-2" data-id="element-57">-</span>}
                  <span data-id="element-58">{componentData.name}</span>
                </div>
                <span className="text-sm" data-id="element-59">{componentData.department}</span>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-gray-200" data-id="element-60">
              <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50" data-id="element-61">
                Abbrechen
              </button>
              <button type="submit" className="px-4 py-2 bg-[#8DC63F] hover:bg-[#7AB929] text-white rounded-lg flex items-center gap-1" data-id="element-62">
                <PlusIcon size={16} data-id="element-63" />
                <span data-id="element-64">Hinzufügen</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
}